import { endOfMonth, endOfWeek, isAfter, startOfDay, startOfMonth, startOfWeek, startOfYear, subDays, subMonths, subWeeks } from 'date-fns';
import { TFunction } from 'i18next';

const clampToToday = (date: Date, today: Date): Date => isAfter(date, today) ? today : date;
const WEEK_OPTIONS = { weekStartsOn: 1 as const }; // Monday as start of the week

interface DateRangeOption {
  label: string;
  getValue: (config: any) => [Date | null, Date | null];
  id: string;
}

export const DATE_ABBR_TO_INTL_FORMAT = {
  DE: 'de-DE',
  EN: 'en-GB',
};

// example output: '2025-02-23 00:00:00.000'
export const formatDateToOpenSearch = (date: Date, isStart: boolean): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day} ${isStart ? '00:00:00.000' : '23:59:59.999'}`;
};

export const isSameYMD = (d1: Date, d2: Date) => d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth()
  && d1.getDate() === d2.getDate();

export const getDateRangerOptions = (fieldID: string, t: TFunction): DateRangeOption[] => {
  const today = startOfDay(new Date());
  const yesterday = subDays(today, 1);
  const lastMonthDate = subMonths(today, 1);
  const startOfLastMonthDate = startOfMonth(lastMonthDate);
  const startOfMonthDate = startOfMonth(today);
  const endOfLastMonth = endOfMonth(lastMonthDate);
  const startOfYearDate = startOfYear(today);
  const startOfLastYear = startOfYear(subMonths(today, 12));
  const endOfLastYear = endOfMonth(subMonths(startOfLastYear, -11));
  const lastWeekRef = subWeeks(today, 1);
  const startOfWeekDate = startOfWeek(lastWeekRef, WEEK_OPTIONS);
  const endOfWeekDate = endOfWeek(lastWeekRef, WEEK_OPTIONS);

  return [
    {
      label: t('dateRangeFilter.DR_TODAY'),
      getValue: () => ([today, today]),
      id: `${fieldID}-today-option`,
    },
    {
      label: t('dateRangeFilter.DR_YESTERDAY'),
      getValue: () => ([yesterday, yesterday]),
      id: `${fieldID}-yesterday-option`,
    },
    {
      label: t('dateRangeFilter.DR_LAST_WEEK'),
      getValue: () => ([startOfWeekDate, clampToToday(endOfWeekDate, today)]),
      id: `${fieldID}-last-week-option`,
    },
    {
      label: t('dateRangeFilter.DR_THIS_MONTH'),
      getValue: () => ([startOfMonthDate, today]),
      id: `${fieldID}-this-month-option`,
    },
    {
      label: t('dateRangeFilter.DR_LAST_MONTH'),
      getValue: () => ([startOfLastMonthDate, clampToToday(endOfLastMonth, today)]),
      id: `${fieldID}-last-month-option`,
    },
    {
      label: t('dateRangeFilter.DR_LAST_30_DAYS'),
      getValue: () => ([subDays(today, 30), today]),
      id: `${fieldID}-last-30-days-option`,
    },
    {
      label: t('dateRangeFilter.DR_THIS_YEAR'),
      getValue: () => ([startOfYearDate, today]),
      id: `${fieldID}-this-year-option`,
    },
    {
      label: t('dateRangeFilter.DR_LAST_YEAR'),
      getValue: () => ([startOfLastYear, clampToToday(endOfLastYear, today)]),
      id: `${fieldID}-last-year-option`,
    },
    {
      label: t('dateRangeFilter.DR_RESET'),
      getValue: () => [null, null],
      id: `${fieldID}-reset-option`,
    },
  ];
};
